import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash, ArrowClockwise, VideoCamera, Check, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * SelfieCapture — captura selfie con:
 *  1. Fix iOS Safari: monta <video> antes de asignar srcObject (useEffect).
 *  2. Guía oval en pantalla para alinear el rostro.
 *  3. Detección de rostro nativa (`window.FaceDetector`) cuando esté disponible
 *     — muestra "Rostro detectado ✓" en tiempo real y bloquea "Tomar foto"
 *     si no hay rostro (evita fotos vacías/oscuras).
 *     En navegadores sin FaceDetector (Safari, Firefox) la detección se
 *     omite silenciosamente y solo se muestra la guía visual.
 */
export default function SelfieCapture({ value, onChange, testid = "selfie" }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const detectLoopRef = useRef(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState("");
  // face detection state: 'unknown' (loading), 'ok', 'not_found', 'unsupported'
  const [faceState, setFaceState] = useState("unknown");
  const faceSupported = typeof window !== "undefined" && "FaceDetector" in window;

  const stopDetectLoop = useCallback(() => {
    if (detectLoopRef.current) {
      cancelAnimationFrame(detectLoopRef.current);
      detectLoopRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    stopDetectLoop();
    const s = streamRef.current;
    if (s) { s.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch (_e) { /* noop */ } }
    setActive(false);
    setFaceState("unknown");
  }, [stopDetectLoop]);

  useEffect(() => () => stop(), [stop]);

  // Detección de rostro periódica (2 FPS) mientras el video esté activo.
  const detectLoop = useCallback(async () => {
    if (!detectorRef.current || !videoRef.current) return;
    try {
      const faces = await detectorRef.current.detect(videoRef.current);
      setFaceState(faces.length > 0 ? "ok" : "not_found");
    } catch (_e) {
      // Un detector puede fallar en el primer frame — ignorar
    }
    // 500 ms es suficiente para feedback en vivo sin fundir CPU/batería
    detectLoopRef.current = setTimeout(() => {
      detectLoopRef.current = requestAnimationFrame(detectLoop);
    }, 500);
  }, []);

  // iOS fix + arranque de detector de rostro cuando el video ya está montado.
  useEffect(() => {
    if (!active) return;
    const s = streamRef.current;
    const v = videoRef.current;
    if (!s || !v) return;
    try {
      v.srcObject = s;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch (e) {
      setError(e.message || "No se pudo iniciar el video");
      return;
    }
    // Setup FaceDetector cuando el video empiece a reproducirse
    if (faceSupported) {
      try {
        detectorRef.current = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
        setFaceState("not_found");
        v.addEventListener("playing", () => detectLoop(), { once: true });
      } catch (_e) {
        setFaceState("unsupported");
      }
    } else {
      setFaceState("unsupported");
    }
    return stopDetectLoop;
  }, [active, faceSupported, detectLoop, stopDetectLoop]);

  const start = async () => {
    setError("");
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Este navegador no soporta cámara (usa Safari/Chrome actualizado).");
      toast.error("Cámara no disponible en este navegador");
      return;
    }
    const tryGetStream = async () => {
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 360 } },
          audio: false,
        });
      } catch (_e) {
        return await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
    };
    try {
      const stream = await tryGetStream();
      streamRef.current = stream;
      setActive(true);
    } catch (e) {
      const msg = e.name === "NotAllowedError"
        ? "Permiso de cámara denegado. Actívala en Ajustes > Safari > Cámara."
        : (e.message || "No se pudo acceder a la cámara");
      setError(msg);
      toast.error("Cámara denegada o no disponible — la firma puede continuar sin selfie");
    }
  };

  const snap = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    // Si el navegador soporta detección y no ve rostro, bloquea la foto.
    if (faceSupported && faceState === "not_found") {
      toast.error("No se detecta rostro — alinea tu cara dentro del óvalo antes de tomar la foto.");
      return;
    }
    c.width = v.videoWidth || 480;
    c.height = v.videoHeight || 360;
    const ctx = c.getContext("2d");
    ctx.translate(c.width, 0); ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.72);
    onChange?.(dataUrl);
    stop();
  };

  const retake = () => { onChange?.(""); start(); };

  const canSnap = !faceSupported || faceState === "ok" || faceState === "unsupported";

  return (
    <div className="border border-slate-200 rounded-lg p-2 bg-white" data-testid={`${testid}-wrapper`}>
      {!value && !active && (
        <button
          onClick={start}
          data-testid={`${testid}-start`}
          className="w-full flex items-center justify-center gap-1.5 py-3 text-sm font-semibold text-slate-600 hover:text-blue-800 hover:bg-blue-50 rounded"
        >
          <Camera size={16} weight="bold" /> Añadir selfie opcional
        </button>
      )}
      {active && !value && (
        <div className="space-y-2">
          <div className="relative">
            <video ref={videoRef} className="w-full h-48 object-cover rounded bg-slate-900 -scale-x-100"
                    muted autoPlay playsInline data-testid={`${testid}-video`} />
            {/* Guía oval para alinear rostro */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className={`w-32 h-40 rounded-[50%] border-2 border-dashed transition-colors ${
                faceState === "ok" ? "border-emerald-400 shadow-[0_0_20px_rgba(52,211,153,0.5)]"
                  : faceState === "not_found" ? "border-amber-300 animate-pulse"
                  : "border-white/70"
              }`} data-testid={`${testid}-face-guide`} />
            </div>
            {/* Badge de estado de detección */}
            {faceSupported && (
              <div className="absolute top-2 left-2">
                {faceState === "ok" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600/90 text-white text-[10px] font-bold px-2 py-1"
                         data-testid={`${testid}-face-ok`}>
                    <Check size={11} weight="bold" /> Rostro detectado
                  </span>
                )}
                {faceState === "not_found" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/95 text-slate-950 text-[10px] font-bold px-2 py-1"
                         data-testid={`${testid}-face-missing`}>
                    <Warning size={11} weight="fill" /> Alinea tu rostro
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={snap} disabled={!canSnap}
                     data-testid={`${testid}-snap`}
                     className={`flex-1 gap-1 ${canSnap ? "bg-blue-900 hover:bg-blue-800" : "bg-slate-400 cursor-not-allowed"}`}
                     title={canSnap ? "" : "Alinea tu rostro dentro del óvalo"}>
              <VideoCamera size={14} weight="fill" /> Tomar foto
            </Button>
            <Button size="sm" variant="outline" onClick={stop} data-testid={`${testid}-cancel`} className="border-slate-200">Cancelar</Button>
          </div>
        </div>
      )}
      {value && (
        <div className="space-y-2">
          <img src={value} alt="selfie" className="w-full h-48 object-cover rounded" data-testid={`${testid}-preview`} />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={retake} data-testid={`${testid}-retake`} className="gap-1"><ArrowClockwise size={12} weight="bold" /> Repetir</Button>
            <Button size="sm" variant="outline" onClick={() => onChange?.("")} data-testid={`${testid}-clear`}
                    className="gap-1 border-red-200 text-red-700 hover:bg-red-50"><Trash size={12} weight="bold" /> Quitar</Button>
          </div>
        </div>
      )}
      {error && !active && !value && (
        <p className="text-[10px] text-amber-700 mt-1 italic">Cámara no disponible ({error}). La firma se puede guardar sin selfie.</p>
      )}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
